import { z } from "zod/v4";

import {
  AgenticLogicCartDataDescriptions,
  AgenticLogicCartLineDescriptions,
  AgenticLogicCartLineFlagsDescriptions,
  AgenticLogicSelectedOptionDescriptions,
} from "../../../descriptions/agentic-logic.describe";
import { assertDescriptions } from "../../../descriptions/types";
import {
  ShopifyCollectionGidSchema,
  ShopifyProductGidSchema,
  ShopifyVariantGidSchema,
} from "../../../schemas/shopify-gid";

export const AgenticLogicSelectedOptionSchema = z
  .object({
    name: z.string().describe(AgenticLogicSelectedOptionDescriptions.name),
    value: z.string().describe(AgenticLogicSelectedOptionDescriptions.value),
  })
  .describe(AgenticLogicSelectedOptionDescriptions._schema);

export type AgenticLogicSelectedOption = z.infer<
  typeof AgenticLogicSelectedOptionSchema
>;

export const AgenticLogicCartLineFlagsSchema = z
  .object({
    giftCard: z
      .boolean()
      .nullable()
      .describe(AgenticLogicCartLineFlagsDescriptions.giftCard),
    syntheticGift: z
      .boolean()
      .nullable()
      .describe(AgenticLogicCartLineFlagsDescriptions.syntheticGift),
    bundleChild: z
      .boolean()
      .nullable()
      .describe(AgenticLogicCartLineFlagsDescriptions.bundleChild),
    igUpsell: z
      .boolean()
      .nullable()
      .describe(AgenticLogicCartLineFlagsDescriptions.igUpsell),
  })
  .describe(AgenticLogicCartLineFlagsDescriptions._schema);

export type AgenticLogicCartLineFlags = z.infer<
  typeof AgenticLogicCartLineFlagsSchema
>;

export const AgenticLogicCartLineSchema = z
  .object({
    productId: ShopifyProductGidSchema.describe(
      AgenticLogicCartLineDescriptions.productId
    ),
    variantId: ShopifyVariantGidSchema.describe(
      AgenticLogicCartLineDescriptions.variantId
    ),
    quantity: z
      .number()
      .int()
      .positive()
      .describe(AgenticLogicCartLineDescriptions.quantity),
    unitPrice: z
      .number()
      .int()
      .nonnegative()
      .describe(AgenticLogicCartLineDescriptions.unitPrice),
    lineTotal: z
      .number()
      .int()
      .nonnegative()
      .describe(AgenticLogicCartLineDescriptions.lineTotal),
    selectedOptions: z
      .array(AgenticLogicSelectedOptionSchema)
      .describe(AgenticLogicCartLineDescriptions.selectedOptions),
    isSubscriptionLine: z
      .boolean()
      .describe(AgenticLogicCartLineDescriptions.isSubscriptionLine),
    flags: AgenticLogicCartLineFlagsSchema.describe(
      AgenticLogicCartLineDescriptions.flags
    ),
    // CONDITIONALLY HYDRATED. Absent means "not looked up", NOT "none" — the
    // two must stay distinguishable, because a rule that targets on them and
    // reads an unhydrated line has to fail loudly rather than quietly decide
    // the line has no tags. An empty array is a real answer: looked up, none.
    tags: z
      .array(z.string())
      .optional()
      .describe(AgenticLogicCartLineDescriptions.tags),
    collections: z
      .array(ShopifyCollectionGidSchema)
      .optional()
      .describe(AgenticLogicCartLineDescriptions.collections),
  })
  .describe(AgenticLogicCartLineDescriptions._schema);

export type AgenticLogicCartLine = z.infer<typeof AgenticLogicCartLineSchema>;

export const AgenticLogicCartDataSchema = z
  .object({
    currency: z.string().describe(AgenticLogicCartDataDescriptions.currency),
    subtotal: z
      .number()
      .int()
      .nonnegative()
      .describe(AgenticLogicCartDataDescriptions.subtotal),
    itemCount: z
      .number()
      .int()
      .nonnegative()
      .describe(AgenticLogicCartDataDescriptions.itemCount),
    lines: z
      .array(AgenticLogicCartLineSchema)
      .describe(AgenticLogicCartDataDescriptions.lines),
  })
  .describe(AgenticLogicCartDataDescriptions._schema);

export type AgenticLogicCartData = z.infer<typeof AgenticLogicCartDataSchema>;

assertDescriptions<
  typeof AgenticLogicSelectedOptionDescriptions,
  AgenticLogicSelectedOption
>();
assertDescriptions<
  typeof AgenticLogicCartLineFlagsDescriptions,
  AgenticLogicCartLineFlags
>();
assertDescriptions<
  typeof AgenticLogicCartLineDescriptions,
  AgenticLogicCartLine
>();
assertDescriptions<
  typeof AgenticLogicCartDataDescriptions,
  AgenticLogicCartData
>();
